// Extrait de ClinicAdminDashboard.js (handover bloc F3-B) — présentationnel ; les 3 appels api inline (archiver/restaurer/supprimer) sont conservés tels quels.
import api from "@/api";
import { toast } from "sonner";
import { BookOpen, Plus, Edit2, Archive, RefreshCw, Trash2 } from "lucide-react";

export default function ClinicEducationTab({
  educationContents,
  eduStatusFilter,
  onStatusFilterChange,
  onOpenCreate,
  onEdit,
  onRefresh,
}) {
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-emerald-600" />
          Contenus éducatifs
        </h3>
        <button
          onClick={onOpenCreate}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nouveau contenu
        </button>
      </div>

      {/* Status filter */}
      <div className="flex gap-2">
        {[
          ["ALL", "Tous"],
          ["PUBLISHED", "Publiés"],
          ["DRAFT", "Brouillons"],
          ["ARCHIVED", "Archivés"],
        ].map(([value, label]) => (
          <button
            key={value}
            onClick={() => onStatusFilterChange(value)}
            data-testid={`edu-filter-${value.toLowerCase()}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              eduStatusFilter === value
                ? "bg-emerald-100 text-emerald-700"
                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
            }`}
          >
            {label} ({value === "ALL" 
              ? educationContents.length 
              : educationContents.filter(c => c.status === value).length})
          </button>
        ))}
      </div>

      {/* Content list */}
      <div className="dash-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-4 font-semibold">Titre</th>
              <th className="text-left p-4 font-semibold">Type</th>
              <th className="text-left p-4 font-semibold">Catégorie</th>
              <th className="text-left p-4 font-semibold">Statut</th>
              <th className="text-center p-4 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {educationContents
              .filter(c => eduStatusFilter === "ALL" || c.status === eduStatusFilter)
              .map(content => (
              <tr key={content.id} className={`hover:bg-slate-50 transition-colors ${content.status === "ARCHIVED" ? "opacity-60" : ""}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{content.title}</p>
                      <p className="text-xs text-slate-500 line-clamp-1">{content.shortDescription}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    content.contentType === "VIDEO" ? "bg-red-100 text-red-700" :
                    content.contentType === "ARTICLE" ? "bg-blue-100 text-blue-700" :
                    "bg-purple-100 text-purple-700"
                  }`}>
                    {content.contentType === "VIDEO" ? "Vidéo" :
                     content.contentType === "ARTICLE" ? "Article" : "Pack"}
                  </span>
                </td>
                <td className="p-4">
                  <span className="text-slate-600">{content.category}</span>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    content.status === "PUBLISHED" ? "bg-green-100 text-green-700" :
                    content.status === "DRAFT" ? "bg-slate-100 text-slate-600" :
                    "bg-red-100 text-red-700"
                  }`}>
                    {content.status === "PUBLISHED" ? "Publié" :
                     content.status === "DRAFT" ? "Brouillon" : "Archivé"}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {content.status !== "ARCHIVED" ? (
                      <>
                        <button
                          onClick={() => onEdit(content)}
                          className="p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={async () => {
                            if (confirm("Archiver ce contenu ? Il ne sera plus visible par les patients.")) {
                              try {
                                await api.delete(`/education/admin/contents/${content.id}`);
                                toast.success("Contenu archivé");
                                onRefresh();
                              } catch { toast.error("Erreur"); }
                            }
                          }}
                          className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Archiver"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/education/admin/contents/${content.id}/restore`);
                            toast.success("Contenu restauré");
                            onRefresh();
                          } catch { toast.error("Erreur"); }
                        }}
                        className="p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Restaurer"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (confirm("Supprimer définitivement ce contenu ? Cette action est irréversible.")) {
                          try {
                            await api.delete(`/education/admin/contents/${content.id}/permanent`);
                            toast.success("Contenu supprimé définitivement");
                            onRefresh();
                          } catch { toast.error("Erreur"); }
                        }
                      }}
                      className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer définitivement"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {educationContents.filter(c => eduStatusFilter === "ALL" || c.status === eduStatusFilter).length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>{eduStatusFilter === "ALL" ? "Aucun contenu éducatif" : `Aucun contenu ${eduStatusFilter === "PUBLISHED" ? "publié" : eduStatusFilter === "DRAFT" ? "en brouillon" : "archivé"}`}</p>
          </div>
        )}
      </div>
    </div>
  );
}
